import { ENTITY_AUDIT_REFETCH_INTERVAL_MS } from "./entity-audit.js";

/**
 * How long the throttle below holds the floor open after firing.
 *
 * Deliberately the SAME constant the poll uses rather than a new number: it is
 * what makes the worst case of push identical to the request rate polling
 * already produces, so this feature can never cost more than what it replaces.
 * If the two drifted apart, a busy tenant could be refetched faster than the
 * timer it was supposed to relieve.
 */
export const ENTITY_AUDIT_PUSH_THROTTLE_MS = ENTITY_AUDIT_REFETCH_INTERVAL_MS;

/**
 * The app-wide audit-change bus.
 *
 * Framework-free ON PURPOSE. `@tanstack/react-query` is not a dependency of
 * this package and should not become one (see the note in
 * `entity-audit-merge.ts`), so this module owns the transport-shaped half —
 * fan-out and throttling — and each feature's history hook owns the
 * invalidation, which is the only part that needs a QueryClient.
 *
 * It exists because the socket lives in the shell and the rails live in four
 * feature packages that may not import the shell. The nudge carries nothing, so
 * a listener is a bare callback: everything a consumer needs to know is "re-read
 * your own feed now".
 */
type Listener = () => void;
type ListenerGroup = { readonly scope: object; readonly key: string };

const listeners = new Map<Listener, ListenerGroup | undefined>();
let lastFiredAt = Number.NEGATIVE_INFINITY;
let trailing: ReturnType<typeof setTimeout> | null = null;

function fire(): void {
  const firedGroups = new Map<object, Set<string>>();
  /* A throwing listener must not stop the others, and must not propagate into
     the socket's message handler — the socket is an accelerator and may never
     take the connection down over what a subscriber did with a nudge. */
  for (const [listener, group] of [...listeners]) {
    if (group && firedGroups.get(group.scope)?.has(group.key)) continue;
    try {
      listener();
      if (group) {
        const keys = firedGroups.get(group.scope) ?? new Set<string>();
        keys.add(group.key);
        firedGroups.set(group.scope, keys);
      }
    } catch {
      // Deliberately swallowed; the listener's own feed is its business.
    }
  }
}

/**
 * Subscribe to audit-change nudges. Returns its own unsubscribe.
 * Consumers with the same scope/key perform one callback per delivered nudge;
 * each feature still owns its refresh policy and the bus has no query dependency.
 */
export function subscribeEntityAuditChanged(
  listener: Listener,
  group?: ListenerGroup,
): () => void {
  listeners.set(listener, group);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * A nudge arrived. LEADING-EDGE throttled, floored at the poll interval.
 *
 * The shape matters more than it looks. A trailing debounce was the obvious
 * choice and is wrong here: at a ~2s debounce a rail could refetch 30 times a
 * minute, which is WORSE than the 12/min poll this replaces, so a busy tenant
 * would have been made slower by a feature meant to relieve it.
 *
 * Leading-edge instead:
 *  - the first nudge after a quiet spell fires IMMEDIATELY, which is the entire
 *    point — a quiet tenant sees true push latency;
 *  - further nudges inside the window are collapsed into at most ONE trailing
 *    fire at the window's end, so nothing is silently dropped;
 *  - the worst case is therefore exactly today's request rate, never more.
 *
 * One throttle for the whole app rather than one per hook: N mounted rails must
 * share a budget, or the bound multiplies by the number of open rails.
 */
export function publishEntityAuditChanged(): void {
  const now = Date.now();
  const sinceLast = now - lastFiredAt;

  if (sinceLast >= ENTITY_AUDIT_PUSH_THROTTLE_MS) {
    /* Drop an OVERDUE trailing timer before firing. It was scheduled for
       `lastFiredAt + THROTTLE_MS`, which this branch has already passed, so it
       can only still be pending because the timer ran late — a background tab
       clamps timers to a second or more, and a blocked event loop does the same.
       Leaving it queued would fire twice inside one window and break the bound
       this whole function exists to hold. */
    if (trailing !== null) {
      clearTimeout(trailing);
      trailing = null;
    }
    lastFiredAt = now;
    fire();
    return;
  }

  // Already one queued for this window — collapse into it.
  if (trailing !== null) return;

  trailing = setTimeout(() => {
    trailing = null;
    lastFiredAt = Date.now();
    fire();
  }, ENTITY_AUDIT_PUSH_THROTTLE_MS - sinceLast);
}

/**
 * Clears the throttle window and any queued trailing fire.
 *
 * For TESTS. The throttle is module state, so a test asserting the leading
 * edge would otherwise inherit the previous test's window and see its first
 * nudge collapsed.
 *
 * An earlier version of this docblock also claimed a sign-out purpose. There
 * is no such caller — every call site in the repo is a spec — and a function
 * that documents a production role nothing performs invites someone to trust
 * that the app already resets this on sign-out. It does not need to: the
 * throttle holds a timestamp and a timer handle, never anything about a user.
 *
 * Does not remove listeners — those are owned by whoever subscribed, which is
 * why the specs here unsubscribe in `afterEach` rather than relying on this.
 */
export function resetEntityAuditPushThrottle(): void {
  if (trailing !== null) {
    clearTimeout(trailing);
    trailing = null;
  }
  lastFiredAt = Number.NEGATIVE_INFINITY;
}
